"use client"

import { useState } from "react"
import { ChevronDown, ThumbsUp, ThumbsDown } from "lucide-react"
import { env } from "~/env"
import StarRating from "./StarRating"

type ReviewFormProps = {
  eventId: string
  eventTitle: string
  eventDate: string
  accessToken: string
  onSubmit?: () => void
  onCancel?: () => void
}

const TAG_GROUPS = [
  {
    label: "Experience",
    tags: ["TRANSFORMATIVE", "INSPIRING", "GROUNDING", "FUN", "DEEP", "EDUCATIONAL", "HEALING", "RELAXING", "EMPOWERING", "PLAYFUL", "INTIMATE", "CREATIVE"],
  },
  {
    label: "Facilitator",
    tags: ["VERY_PRESENT", "CLEAR_GUIDANCE", "SUPPORTIVE", "AUTHENTIC", "PROFESSIONAL", "WARM", "INTUITIVE", "KNOWLEDGEABLE", "COMPASSIONATE"],
  },
  {
    label: "Community",
    tags: ["WELCOMING_GROUP", "SAFE_SPACE", "GREAT_CONNECTIONS", "INCLUSIVE", "INTIMATE_GROUP", "DIVERSE"],
  },
]

const TAG_LABELS: Record<string, string> = {
  TRANSFORMATIVE: "Transformative",
  INSPIRING: "Inspiring",
  GROUNDING: "Grounding",
  FUN: "Fun",
  DEEP: "Deep",
  EDUCATIONAL: "Educational",
  HEALING: "Healing",
  RELAXING: "Relaxing",
  EMPOWERING: "Empowering",
  PLAYFUL: "Playful",
  INTIMATE: "Intimate",
  CREATIVE: "Creative",
  VERY_PRESENT: "Very Present",
  CLEAR_GUIDANCE: "Clear Guidance",
  SUPPORTIVE: "Supportive",
  AUTHENTIC: "Authentic",
  PROFESSIONAL: "Professional",
  WARM: "Warm",
  INTUITIVE: "Intuitive",
  KNOWLEDGEABLE: "Knowledgeable",
  COMPASSIONATE: "Compassionate",
  WELCOMING_GROUP: "Welcoming Group",
  SAFE_SPACE: "Safe Space",
  GREAT_CONNECTIONS: "Great Connections",
  INCLUSIVE: "Inclusive",
  INTIMATE_GROUP: "Intimate Group",
  DIVERSE: "Diverse",
}

export default function ReviewForm({
  eventId, eventTitle, eventDate, accessToken, onSubmit, onCancel,
}: ReviewFormProps) {
  const [overallRating, setOverallRating] = useState(0)
  const [spaceRating, setSpaceRating] = useState(0)
  const [guidanceRating, setGuidanceRating] = useState(0)
  const [organisationRating, setOrganisationRating] = useState(0)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null)
  const [reviewText, setReviewText] = useState("")
  const [privateFeedback, setPrivateFeedback] = useState("")
  const [showSubRatings, setShowSubRatings] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    )
  }

  const handleSubmit = async () => {
    if (overallRating === 0) {
      setError("Please rate your overall experience")
      return
    }
    setError("")
    setSubmitting(true)

    try {
      const body: Record<string, unknown> = { overall_rating: overallRating }
      if (spaceRating > 0) body.space_rating = spaceRating
      if (guidanceRating > 0) body.guidance_rating = guidanceRating
      if (organisationRating > 0) body.organisation_rating = organisationRating
      if (selectedTags.length > 0) body.tags = selectedTags
      if (wouldRecommend !== null) body.would_recommend = wouldRecommend
      if (reviewText.trim()) body.review_text = reviewText.trim()
      if (privateFeedback.trim()) body.private_feedback = privateFeedback.trim()

      const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/reviews/events/${eventId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.message || "Failed to submit review")
      }

      onSubmit?.()
    } catch (err: any) {
      setError(err.message || "Something went wrong")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-emphasis">{eventTitle}</h3>
        <p className="text-sm text-neutral">{eventDate}</p>
      </div>

      {/* Overall Rating */}
      <div>
        <label className="text-sm font-medium text-neutral-emphasis mb-2 block">
          Overall Experience <span className="text-red-500">*</span>
        </label>
        <StarRating value={overallRating} onChange={setOverallRating} size="lg" />
      </div>

      {/* Sub-ratings (collapsible) */}
      <div>
        <button
          type="button"
          onClick={() => setShowSubRatings(!showSubRatings)}
          className="flex items-center gap-1 text-sm text-neutral hover:text-neutral-emphasis transition-colors"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${showSubRatings ? "rotate-180" : ""}`} />
          Rate specific areas
        </button>
        {showSubRatings && (
          <div className="mt-3 space-y-3 pl-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral">Space & Atmosphere</span>
              <StarRating value={spaceRating} onChange={setSpaceRating} size="sm" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral">Guidance & Facilitation</span>
              <StarRating value={guidanceRating} onChange={setGuidanceRating} size="sm" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-neutral">Organisation</span>
              <StarRating value={organisationRating} onChange={setOrganisationRating} size="sm" />
            </div>
          </div>
        )}
      </div>

      {/* Quick Tags */}
      <div>
        <label className="text-sm font-medium text-neutral-emphasis mb-2 block">
          What stood out?
        </label>
        {TAG_GROUPS.map((group) => (
          <div key={group.label} className="mb-2">
            <p className="text-xs text-neutral mb-1">{group.label}</p>
            <div className="flex flex-wrap gap-1.5">
              {group.tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    selectedTags.includes(tag)
                      ? "bg-[#DE866D] text-white border-[#DE866D]"
                      : "bg-white text-neutral-emphasis border-neutral-300 hover:border-[#DE866D]"
                  }`}
                >
                  {TAG_LABELS[tag]}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Would You Recommend */}
      <div>
        <label className="text-sm font-medium text-neutral-emphasis mb-2 block">
          Would you recommend this?
        </label>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setWouldRecommend(true)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm transition-colors ${
              wouldRecommend === true
                ? "bg-[#DE866D] text-white border-[#DE866D]"
                : "bg-white text-neutral-emphasis border-neutral-300 hover:border-[#DE866D]"
            }`}
          >
            <ThumbsUp className="h-4 w-4" /> Yes
          </button>
          <button
            type="button"
            onClick={() => setWouldRecommend(false)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm transition-colors ${
              wouldRecommend === false
                ? "bg-neutral-600 text-white border-neutral-600"
                : "bg-white text-neutral-emphasis border-neutral-300 hover:border-neutral-400"
            }`}
          >
            <ThumbsDown className="h-4 w-4" /> Not really
          </button>
        </div>
      </div>

      {/* Written Review */}
      <div>
        <label className="text-sm font-medium text-neutral-emphasis mb-1 block">
          Share your experience
        </label>
        <textarea
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          placeholder="What did you enjoy or what made the experience special?"
          maxLength={2000}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE866D]/50 focus:border-[#DE866D] resize-none"
        />
      </div>

      {/* #1166 FR-3 — nothing shows this to the facilitator; hidden, the
          `private_feedback` API field is kept and stays empty. */}

      {/* Error */}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || overallRating === 0}
          className="flex-1 px-4 py-2.5 bg-[#DE866D] text-white text-sm font-medium rounded-lg hover:bg-[#C97257] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "Submitting..." : "Submit Review"}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2.5 text-sm text-neutral border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  )
}
